import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { UseGetFacilityProfileResponse } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useQueryClient } from "@tanstack/react-query";
import { fetchPlacementsApiUrl } from "@src/appV2/Placements/api/useFetchPaginatedPlacements";
import {
  GET_FACILITY_PROFILES_API_URL,
  GetWorkplacesResponse,
  useGetAllChildWorkplaces,
} from "@src/appV2/FacilityProfile/api/useGetAllChildWorkplaces";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useUpdateWorkplacePlacementSettings } from "@src/appV2/FacilityProfile/api/useUpdateWorkplacePlacementSettings";
import { API_TYPES } from "@src/appV2/api/constants";
import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "@src/appV2/lib/utils";

export function ManageFacilityPlacementsSelect({
  facilityProfile,
}: {
  facilityProfile: UseGetFacilityProfileResponse;
}) {
  const { profile } = useSession();
  const [value, setValue] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const queryClient = useQueryClient();

  // mutation to update the facility placement settings of the child facilities
  const {
    mutateAsync: updateFacilityPlacementSettings,
    isLoading: updateFacilityPlacementSettingsLoading,
  } = useUpdateWorkplacePlacementSettings({
    onSuccess: ({ data }) => {
      setDirty(false);
      showSuccessToast("Updated settings");
      queryClient.setQueryData(
        [GET_FACILITY_PROFILES_API_URL, { parentFacilityId: facilityProfile.userId }],
        (prev: Omit<GetWorkplacesResponse, "links"> | undefined) => {
          if (!prev) {
            return undefined;
          }

          return {
            ...prev,
            data: prev.data.map((facility) => ({
              ...facility,
              attributes: {
                ...facility.attributes,
                placementSettings: {
                  ...facility.attributes.placementSettings,
                  managedByParentWorkplace: data.find((d) => d.id === facility.id)?.attributes
                    .managedByParentWorkplace,
                },
              },
            })),
          };
        }
      );

      void queryClient.invalidateQueries({
        queryKey: [GET_FACILITY_PROFILES_API_URL],
      });
      void queryClient.invalidateQueries({
        queryKey: [fetchPlacementsApiUrl(facilityProfile.userId)],
      });
    },
    onError: () => {
      showErrorToast("Error updating facility profile");
    },
    onSettled: () => {
      void queryClient.resetQueries({
        queryKey: [fetchPlacementsApiUrl(facilityProfile.userId)],
      });
    },
  });

  // fetch the child facilities
  const { data: childWorkplaces, isSuccess: isGetChildWorkplacesSuccess } =
    useGetAllChildWorkplaces({
      parentFacilityId: profile.userId,
    });

  // set the local value when the child facilities change (initial load, or query refetch)
  useEffect(() => {
    setValue(
      childWorkplaces?.data
        .filter((facility) => facility.attributes.placementSettings?.managedByParentWorkplace)
        .map((facility) => facility.id) ?? []
    );
  }, [childWorkplaces]);

  // perform a server update when the value changes
  const onUpdateValue = useCallback(
    async (selectedIds: string[]) => {
      if (!dirty) {
        return;
      }

      if (!childWorkplaces) {
        throw new Error("Attempted to update facility placement settings without child facilities");
      }

      const data = childWorkplaces.data.map((childWorkplace) => ({
        type: API_TYPES.workplace,
        id: childWorkplace.id,
        attributes: {
          managedByParentWorkplace: selectedIds.includes(childWorkplace.id),
        },
      }));

      await updateFacilityPlacementSettings({ data });
    },
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirty, setDirty, childWorkplaces, updateFacilityPlacementSettings]
  );

  // trigger an update when the debounced value changes
  useDebounce({
    value,
    onUpdateValue,
  });

  // options which are displayed in the dropdown
  const options =
    childWorkplaces?.data.map((facility) => ({
      value: facility.id,
      label: facility.attributes.name ?? "",
    })) ?? [];

  if (!isGetChildWorkplacesSuccess || childWorkplaces?.data.length === 0) {
    return null;
  }

  return (
    <FormControl>
      <FormLabel>Manage jobs for my child facilities</FormLabel>
      <Select
        disabled={updateFacilityPlacementSettingsLoading}
        onChange={async (selected) => {
          setDirty(true);
          setValue(selected.target.value as string[]);
        }}
        multiple
        value={value}
        input={<OutlinedInput aria-label="Select facilities" size="small" />}
        label="Select facilities"
        sx={{ width: 300 }}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <Text>None</Text>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {/* FIXME: Automatically disabled during ESLint config update */}
              {/* eslint-disable-next-line @typescript-eslint/no-shadow */}
              {selected.map((value) => {
                const option = options.find((opt) => opt.value === value);
                return option ? <Chip size="small" key={value} label={option.label} /> : null;
              })}
            </Box>
          )
        }
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={updateFacilityPlacementSettingsLoading}
          >
            <Checkbox checked={value.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText sx={{ marginLeft: 0 }}>
        Select the facilities that you want to manage jobs for.
      </FormHelperText>
    </FormControl>
  );
}
